import { useEffect, useRef, useState } from 'react';
import CoinMedia from '../CoinMedia/CoinMedia';
import { isSignatureCoin, shareCoin } from '../../utils/coinMedia';
import styles from './CoinCelebration.module.css';

// `coinMedia` is the API's normalized media object; when it is an animated
// Signature coin the animation plays automatically as the centrepiece.
export function CoinCelebration({ onComplete, coinLabel, coinImageUrl, coinMedia = null, subtitle, shareTitle, shareMessage, shareUrl, onViewCoin }) {
    const coin = { coinImageUrl, coinMedia };
    const signature = isSignatureCoin(coin);
    const containerRef = useRef(null);
    const [showContinueButton, setShowContinueButton] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Create confetti elements
        const container = containerRef.current;
        if (!container) return;

        const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
        const confettiCount = 50;

        for (let i = 0; i < confettiCount; i++) {
            const confetti = document.createElement('div');
            confetti.className = styles.confetti;

            const size = 8 + Math.random() * 8;
            const startX = Math.random() * window.innerWidth;
            const endX = startX + (Math.random() - 0.5) * 200;
            const rotation = Math.random() * 720;
            const delay = Math.random() * 300;
            const duration = 2000 + Math.random() * 1000;
            const color = colors[i % colors.length];
            const shape = Math.random() > 0.5 ? '50%' : '0';

            confetti.style.cssText = `
                width: ${size}px;
                height: ${size}px;
                background-color: ${color};
                border-radius: ${shape};
                left: ${startX}px;
                animation: confettiFall ${duration}ms ease-out ${delay}ms forwards;
                --end-x: ${endX}px;
                --rotation: ${rotation}deg;
            `;

            container.appendChild(confetti);
        }

        // Show continue button after coin animation completes (2s animation + 0.5s delay)
        const timer = setTimeout(() => {
            setShowContinueButton(true);
        }, 2500);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    const handleShare = async () => {
        if (!shareUrl) return;
        try {
            const result = await shareCoin({
                coin,
                title: shareTitle || 'Share on Aluvo',
                text: shareMessage,
                url: shareUrl,
            });
            if (result !== 'unsupported') return;

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            } else {
                const input = document.createElement('input');
                input.value = shareUrl;
                input.style.position = 'fixed';
                input.style.opacity = '0';
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.coinContainer}>
                {subtitle && (
                    <p className={styles.subtitle}>{subtitle}</p>
                )}
                {signature && (
                    <p className={styles.signatureHeadline}>💎 Signature Coin Unlocked</p>
                )}
                <div className={`${styles.coin} ${signature ? styles.coinSignature : ''}`}>
                    <CoinMedia
                        coin={coin}
                        animate
                        alt="Coin"
                        className={styles.coinImage}
                        fallback={<span className={styles.coinEmoji}>🪙</span>}
                    />
                    {coinLabel && (
                        <span className={styles.coinLabel}>{coinLabel}</span>
                    )}
                </div>
            </div>

            {showContinueButton && (
                <div className={styles.actionRow}>
                    {shareUrl && (
                        <button
                            className={`${styles.actionButton} ${styles.shareButton}`}
                            onClick={handleShare}
                        >
                            {copied ? 'Copied!' : (signature ? 'Share Coin' : 'Share')}
                        </button>
                    )}
                    {onViewCoin && (
                        <button
                            className={`${styles.actionButton} ${styles.shareButton}`}
                            onClick={onViewCoin}
                        >
                            View Coin
                        </button>
                    )}
                    <button
                        className={`${styles.actionButton} ${styles.continueButton}`}
                        onClick={onComplete}
                    >
                        Continue
                    </button>
                </div>
            )}
        </div>
    );
}
